import { useCallback, useMemo, useState } from 'react'
import Immutable from 'immutable'
import cc from 'classcat'

type Props = Readonly<{
  config: {
    plugins?: Record<string, any>
  }
  element: ImmutableMap
  onElementChange: (newElement: ImmutableMap) => void
  isMultiColumn: WorkspacePluginProps['isMultiColumn']
  editorView: WorkspacePluginProps['editorView']
  editorMode?: WorkspacePluginEditorMode
  editAction?: WorkspacePluginEditAction
  requestModeChange?: (mode: WorkspacePluginProps['editorMode'], editAction: WorkspacePluginEditAction) => void
}>

export default function Element({
  config,
  editorView,
  element: propElement,
  isMultiColumn,
  onElementChange,
  editorMode = 'view',
  editAction = null,
  requestModeChange,
}: Props) {
  const initialData = useMemo(() => propElement.get('data'), [propElement])
  const [localElement, setLocalElement] = useState<ImmutableMap>()

  const element = localElement || propElement

  const emitChange = useCallback(
    (newElement: ImmutableMap) => {
      if (!Immutable.is(newElement, propElement)) {
        onElementChange(newElement)
      }
    },
    [onElementChange, propElement],
  )

  const handlePluginDataChange = useCallback(
    (newPluginData: ImmutableMap) => {
      setLocalElement(element.set('data', newPluginData))
    },
    [element],
  )

  const handlePluginEdit = useCallback(
    (editAction = null) => {
      requestModeChange?.('edit', editAction)
    },
    [requestModeChange],
  )

  const handlePluginCancel = useCallback(() => {
    requestModeChange?.('view', null)
    handlePluginDataChange(initialData)
  }, [handlePluginDataChange, initialData, requestModeChange])

  const handlePluginSave = useCallback(
    (newPluginData: ImmutableMap, keepOpen = false) => {
      const newElement = element.set('data', newPluginData)
      if (!keepOpen) requestModeChange?.('view', null)
      setLocalElement(newElement)
      emitChange(newElement)
    },
    [emitChange, element, requestModeChange],
  )

  const plugin = config.plugins?.[propElement.get('type')]

  if (!plugin) {
    return <div>{`Unknown plugin type ${propElement.get('type')}`}</div>
  }

  const PluginComponent = plugin.component

  return (
    <div className={cc(['dali-grid-element', { 'dali-grid-element-editview': editorView }])}>
      <div className="dali-grid-element-wrapper">
        <PluginComponent
          config={plugin.config}
          editorView={editorView}
          editorMode={editorMode}
          editAction={editAction}
          isMultiColumn={isMultiColumn}
          id={element.get('_id')}
          type={element.get('type')}
          data={element.get('data')}
          onDataChange={handlePluginDataChange}
          onSave={handlePluginSave}
          onEdit={handlePluginEdit}
          onCancel={handlePluginCancel}
        />
      </div>
    </div>
  )
}
